// Maps getArtifacts GraphQL rows to the artifact bag shape the guidance
// detail/overview pages consume (see useAIConversation.getChatOverview).
// Extracted as a pure function so the mapping — including which artifact
// types survive — is unit-testable (BIO-2062).

export interface OverviewArtifact {
  id?: string;
  artifactType?: string;
  artifactSubType?: string;
  html?: string;
  title?: string;
  status?: string;
  downloadUrl?: string;
  caseId?: string;
  /** Corrective-action kind (verbalWarning / writtenWarning / performanceImprovementPlan). */
  type?: string;
}

/**
 * Map getArtifacts rows to the overview artifact bag.
 * Returns null when there is nothing to map: an empty result must NOT be
 * treated as a valid bag, or it clobbers the REST arm's richer response in
 * getChatOverview's race (`[]` is truthy — BIO-2062).
 */
export function mapOverviewArtifacts(
  artifacts: OverviewArtifact[] | null | undefined
): Record<string, Partial<OverviewArtifact>> | null {
  if (!artifacts?.length) {
    return null;
  }

  const mappedArtifacts: Record<string, Partial<OverviewArtifact>> = {};

  artifacts.forEach((artifact) => {
    const type = artifact.artifactType?.toLowerCase();

    if (
      type === 'hr_practices' ||
      type === 'policy' ||
      artifact.artifactSubType === 'policy'
    ) {
      mappedArtifacts.hrPractices = {
        id: artifact.id,
        html: artifact.html,
        title: artifact.title,
        status: artifact.status,
        downloadUrl: artifact.downloadUrl,
      };
    } else if (type === 'implementation_plan') {
      mappedArtifacts.implementationPlan = {
        id: artifact.id,
        html: artifact.html,
        title: artifact.title,
        status: artifact.status,
        downloadUrl: artifact.downloadUrl,
      };
    } else if (type === 'action_plan') {
      mappedArtifacts.actionPlan = {
        id: artifact.id,
        html: artifact.html,
        title: artifact.title,
        status: artifact.status,
        downloadUrl: artifact.downloadUrl,
      };
    } else if (type === 'corrective_action') {
      mappedArtifacts.correctiveAction = {
        id: artifact.id,
        html: artifact.html,
        title: artifact.title,
        status: artifact.status,
        downloadUrl: artifact.downloadUrl,
        caseId: artifact.caseId,
        type: artifact.artifactSubType,
      };
    } else if (type === 'best_practice_summary') {
      mappedArtifacts.bestPracticeSummary = {
        id: artifact.id,
        html: artifact.html,
        status: artifact.status,
      };
    } else if (type === 'comprehensive_guidance') {
      mappedArtifacts.comprehensiveGuidance = {
        id: artifact.id,
        html: artifact.html,
        status: artifact.status,
      };
    }
  });

  return mappedArtifacts;
}
